import ReactMarkdown from "react-markdown";
import * as R from "remeda";
import InlineCode from "@/components/shared/InlineCode";

interface MarkdownProps {
  text: string;
  openLinksInNewTab?: boolean;
  inline?: boolean;
}

const Markdown = ({ text, openLinksInNewTab, inline = true }: MarkdownProps) => (
  <ReactMarkdown
    allowedElements={[
      "a", "p", "strong", "em", "code", "h1", "h2", "h3", "h4", "h5", "h6",
      "ul", "ol", "li", "blockquote", "hr", "img", "pre", "table", "thead",
      "tbody", "tr", "th", "td", "br"
    ]}
    components={{
      a: ({ ...props }) => (
        <a
          className="text-blue-300 hover:underline font-semibold"
          {...R.omit(props, ["node"])}
        />
      ),
      code: ({ inline: isInline, className, children, ...props }) => {
        // Extract language class if it exists (e.g. language-javascript)
        const match = /language-(\w+)/.exec(className || '');
        const language = match ? match[1] : '';

        if (!isInline) {
          return (
            <pre className="bg-gray-800 px-4 py-3 my-2 rounded-lg overflow-auto">
              <code
                className={`font-mono !text-gray-100 text-sm leading-relaxed ${language ? `language-${language}` : ''}`}
                {...props}
              >
                {children}
              </code>
            </pre>
          );
        }

        return <InlineCode {...props}>{children}</InlineCode>;
      },
      h1: ({ children, ...props }) => (
        <h1 className="text-2xl font-semibold mt-4 mb-2" {...props}>
          {children}
        </h1>
      ),
      h2: ({ children, ...props }) => (
        <h2 className="text-xl font-semibold mt-4 mb-2" {...props}>
          {children}
        </h2>
      ),
      h3: ({ children, ...props }) => (
        <h3 className="text-lg font-semibold mt-3 mb-1" {...props}>
          {children}
        </h3>
      ),
      h4: ({ children, ...props }) => (
        <h4 className="text-base font-semibold mt-3 mb-1" {...props}>
          {children}
        </h4>
      ),
      h5: ({ children, ...props }) => (
        <h5 className="text-sm font-semibold mt-2 mb-1" {...props}>
          {children}
        </h5>
      ),
      h6: ({ children, ...props }) => (
        <h6 className="text-xs font-semibold mt-2 mb-1" {...props}>
          {children}
        </h6>
      ),
      ul: ({ children, ...props }) => (
        <ul className="list-disc pl-5 my-2" {...props}>
          {children}
        </ul>
      ),
      ol: ({ children, ...props }) => (
        <ol className="list-decimal pl-5 my-2" {...props}>
          {children}
        </ol>
      ),
      li: ({ children, ...props }) => (
        <li className="my-1" {...props}>
          {children}
        </li>
      ),
      blockquote: ({ children, ...props }) => (
        <blockquote
          className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4"
          {...props}
        >
          {children}
        </blockquote>
      ),
      hr: ({ ...props }) => (
        <hr className="my-4 border-gray-300" {...props} />
      ),
      img: ({ alt, src, ...props }) => (
        <img
          alt={alt}
          src={src}
          className="max-w-full my-4 rounded"
          {...props}
        />
      ),
      table: ({ children, ...props }) => (
        <div className="w-full overflow-x-auto my-4">
          <table
            className="min-w-full border-collapse border border-gray-300"
            {...props}
          >
            {children}
          </table>
        </div>
      ),
      thead: ({ children, ...props }) => (
        <thead className="bg-gray-100" {...props}>
          {children}
        </thead>
      ),
      tbody: ({ children, ...props }) => (
        <tbody {...props}>{children}</tbody>
      ),
      tr: ({ children, ...props }) => (
        <tr className="border-b border-gray-300" {...props}>
          {children}
        </tr>
      ),
      th: ({ children, ...props }) => (
        <th className="px-4 py-2 text-left font-semibold" {...props}>
          {children}
        </th>
      ),
      td: ({ children, ...props }) => (
        <td className="px-4 py-2" {...props}>
          {children}
        </td>
      ),
      ...(inline ? { p: "span" } : {
        p: ({ children, ...props }) => (
          <p className="my-2" {...props}>{children}</p>
        )
      })
    }}
    {...(openLinksInNewTab ? { linkTarget: "_blank" } : {})}
    skipHtml
  >
    {text}
  </ReactMarkdown>
);

export default Markdown;
